import { useMemo, useState } from "react";
import CommonModal from "../components/CommonModal";
import Subheader from "../components/Subheader";

const LOOM_PREVIEW_FLAGS = {
  hide_share: true,
  hideEmbedTopBar: true,
  hide_title: true,
  hide_owner: true,
  hide_speed: true,
  autoplay: false,
  muted: false,
};

const buildLoomUrl = (id, params) => {
  const base = `https://www.loom.com/embed/${id}`;
  if (!params) return base;
  const query = Object.entries(params)
    .map(([key, value]) => `${key}=${value ? "true" : "false"}`)
    .join("&");
  return `${base}?${query}`;
};

const getLoomEmbedUrl = (id) => buildLoomUrl(id);
const getLoomPreviewUrl = (id) => buildLoomUrl(id, LOOM_PREVIEW_FLAGS);

const FOUNDATIONS_LOOM_ID = "f43d5175144040deb4b3a4c97b18bf90";
const SPYSEARCH_LOOM_ID = "0bf816dc09d447659303a64eeca55917";
const PREISWECKER_LOOM_ID = "8b9413f9308246d5b40a5e16a633cb95";
const A2A_LOOM_ID = "eb521aff188d44cbaa4e7a02e923e2e8";
const WHOLESALE_LOOM_ID = "ef53098265834c22ac75df5ba2f5103b";
const DISCORD_PINGS_LOOM_ID = "274fccc2f310472396ae138ead5b9e7e";

const guideVideos = [
  {
    id: "academy-foundations",
    title: "Willkommensvideo",
    module: "Willkommen",
    level: "beginner",
    duration: "12:48",
    description: "Willkommensvideo zum Einstieg in Edge.",
    tags: [],
    url: getLoomEmbedUrl(FOUNDATIONS_LOOM_ID),
    previewUrl: getLoomPreviewUrl(FOUNDATIONS_LOOM_ID),
  },
  {
    id: "academy-spysearch",
    title: "Spy Search Guide",
    module: "Spy Search",
    level: "intermediate",
    duration: "09:32",
    description: "Kurzer Guide zum Modul Spy Search.",
    tags: [],
    url: getLoomEmbedUrl(SPYSEARCH_LOOM_ID),
    previewUrl: getLoomPreviewUrl(SPYSEARCH_LOOM_ID),
  },
  {
    id: "academy-preiswecker",
    title: "Preisweckerlisten erstellen | Guide",
    module: "Preiswecker",
    level: "beginner",
    duration: "08:17",
    description: "Preiswecker Guide.",
    tags: [],
    url: getLoomEmbedUrl(PREISWECKER_LOOM_ID),
    previewUrl: getLoomPreviewUrl(PREISWECKER_LOOM_ID),
  },
  {
    id: "academy-flips",
    title: "A2A Flips Guide",
    module: "A2A Flips",
    level: "advanced",
    duration: "13:04",
    description: "Kurzer Guide zum Modul A2A Flips.",
    tags: [],
    url: getLoomEmbedUrl(A2A_LOOM_ID),
    previewUrl: getLoomPreviewUrl(A2A_LOOM_ID),
  },
  {
    id: "academy-wholesale",
    title: "Wholesale Scanner Guide",
    module: "Wholesale Scanner",
    level: "intermediate",
    duration: "10:21",
    description: "Kurzer Guide zum Wholesale Scanner.",
    tags: [],
    url: getLoomEmbedUrl(WHOLESALE_LOOM_ID),
    previewUrl: getLoomPreviewUrl(WHOLESALE_LOOM_ID),
  },
  {
    id: "academy-discord",
    title: "Discord Pings Guide",
    module: "Discord",
    level: "advanced",
    duration: "11:09",
    description: "Kurzer Guide zu Discord Pings.",
    tags: [],
    url: getLoomEmbedUrl(DISCORD_PINGS_LOOM_ID),
    previewUrl: getLoomPreviewUrl(DISCORD_PINGS_LOOM_ID),
  },
];

const GuideCard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeVideo, setActiveVideo] = useState(null);

  const filteredVideos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return guideVideos.filter((video) => {
      const matchesTerm =
        !term ||
        `${video.title} ${video.module} ${video.description} ${video.tags.join(" ")}`.toLowerCase().includes(term);
      return matchesTerm;
    });
  }, [searchTerm]);

  return (
    <>
      <div className="flex flex-col flex-1 min-h-0">
        <section className="px-4 md:px-8 py-4 border-b border-borderBottom bg-background">
          <div className="space-y-1">
            <Subheader>
              Edge <em>Academy</em>
            </Subheader>
            <p className="text-xs md:text-sm text-textPrimary">
              Alle Guides & Walkthroughs auf einen Blick – nutze Suche oder Filter, um dein Modul zu finden.
            </p>
          </div>
        </section>

        <section className="px-6 md:px-10 py-8 space-y-8">
          <div className="w-full max-w-lg">
            <label htmlFor="guide-search" className="text-xs uppercase tracking-wider text-textPrimary block mb-2">
              Suche in der Academy
            </label>
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-borderBottom bg-bgColor focus-within:border-primary transition">
              <i className="bx bx-search text-lg text-textPrimary" aria-hidden="true"></i>
              <input
                id="guide-search"
                type="text"
                placeholder="Suche nach Modul, Thema oder Tag"
                className="bg-transparent flex-1 outline-none border-none focus:border-none text-sm text-textTitle dark:text-white placeholder:text-textPrimary/60 appearance-none"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {filteredVideos.length === 0 && (
              <div className="col-span-full text-center text-textPrimary py-12 border border-dashed border-borderBottom rounded-2xl">
                Keine Videos gefunden. Passe Filter oder Suchbegriff an.
              </div>
            )}

            {filteredVideos.map((video) => (
              <article
                key={video.id}
                className="flex flex-col bg-bgColor border border-borderBottom rounded-2xl p-5 shadow-[0_20px_70px_rgba(0,0,0,0.08)]"
              >
                <div className="relative aspect-video rounded-xl border border-borderBottom overflow-hidden bg-[linear-gradient(135deg,rgba(61,131,255,0.25),rgba(103,15,172,0.35))]">
                  {video.previewUrl && (
                    <iframe
                      src={video.previewUrl}
                      title={`${video.title} Vorschau`}
                      className="absolute inset-0 h-full w-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen; encrypted-media"
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                  )}
                  <button
                    type="button"
                    className="absolute inset-0 hover:bg-black/15 focus-visible:bg-black/20 transition"
                    onClick={() => setActiveVideo(video)}
                    aria-label={`${video.title} öffnen`}
                  >
                    <span className="sr-only">Video öffnen</span>
                  </button>
                  <span className="absolute bottom-3 right-3 text-xs px-2 py-1 rounded-full bg-black/60 text-white">
                    {video.duration}
                  </span>
                </div>

                <div className="mt-5 flex flex-col gap-3 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-widest text-primary">
                    <span>{video.module}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-textTitle leading-tight">{video.title}</h3>
                  <p className="text-sm text-textPrimary flex-1">{video.description}</p>
                </div>

                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 text-primary font-semibold text-sm"
                    onClick={() => setActiveVideo(video)}
                  >
                    Ansehen
                    <i className="bx bx-right-arrow-alt text-lg" aria-hidden="true"></i>
                  </button>
                </div>
                  </article>
            ))}
          </div>
        </section>
      </div>

      <CommonModal
        isOpen={Boolean(activeVideo)}
        closeModal={() => setActiveVideo(null)}
        overflowY={false}
        width="90vw"
        maxWidth="960px"
      >
        {activeVideo && (
          <div className="bg-background rounded-[12px] p-4 md:p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-textPrimary">{activeVideo.module}</p>
                <h2 className="text-2xl font-semibold text-textTitle leading-tight">{activeVideo.title}</h2>
              </div>
              <button
                type="button"
                onClick={() => setActiveVideo(null)}
                className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition"
                aria-label="Guide Video schließen"
              >
                <i className="bx bx-x text-2xl"></i>
              </button>
            </div>
            <p className="text-sm text-textPrimary">{activeVideo.description}</p>
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                src={activeVideo.url}
                title={activeVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-xl"
              ></iframe>
            </div>
          </div>
        )}
      </CommonModal>
    </>
  );
};

export default GuideCard;
